import React, {useState, memo} from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import Dropdown from '@cosmos/dropdown'

/* Components */
import HbLink from '../Link/HbLink'
import HbHorizontalScrollView from '../Scroll/horizontal/HbHorizontalScrollView'
import HbBreadCrumbStructuredData from './HbBreadCrumbStructuredData'

/* Utils */
import {isAbsoluteUrl} from '../../common/url/urlUtils'

/* Constants */
import {BREADCRUMB_SIZES} from './HbBreadbrumbConstants'
import {DAVINCI_EVENT_NAMES} from '../../modules/analytics/davinci/davinciConstants'

/* Configs */
import appConfig from '../../appConfig'

/* Styles */
import s from './hb-bread-crumb-view.scss'

const HbBreadCrumbView = memo(({
  className,
  activeItemId,
  itemConfigs,
  isSchemaStructuredActive,
  rootIconEnabled,
  size,
  lastItemDisabled,
  forceRedirect,
  location,
  enableChildBreadcrumb,
  showPLPRedesignLayout
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const getStructData = () => {
    const {baseUrl} = appConfig;

    return itemConfigs.map((data, index) => {
      let url;
      if (isAbsoluteUrl(data.url)) {
        url = data.url;
      } else {
        url = data.url === '/' ? baseUrl : `${baseUrl}${data.url}`
      }

      return {
        '@type': 'ListItem',
        position: index + 1,
        name: data.labelText,
        item: url
      }
    })
  }

  const sendDavinciEvent = (text, index) => {
    if (lastItemDisabled && index === itemConfigs.length - 1) {
      return
    }

    window?.hepsiBus?.sendToDavinci(DAVINCI_EVENT_NAMES.LINK_CLICK, {
      page_type: window?.hepsiBus?.parsers?.getPageType() || '',
      page_value: window?.hepsiBus?.parsers?.getPageValue() || '',
      location: 'Breadcrumb',
      text: text,
      position: index,
      url: itemConfigs[index]?.url || ''
    })
  }

  const renderRootLink = (rootItemConfig) => {
    return (
      <li
        key={'root'}
        className={cx(s.linkListItem, s.rootLinkListItem)}
        onClick={() => sendDavinciEvent(rootItemConfig.labelText)}>
        <a href={rootItemConfig.url} className={s.link} title={'Anasayfa'}>
          {rootIconEnabled && <div className={s.rootLinkIcon}/>}
          <span className={s.linkContent}>{rootItemConfig.labelText}</span>
        </a>
      </li>
    )
  }

  const getBreadcrumbLinkItem = ({item, index, isParent}) => {
    return (
      <a
        href={lastItemDisabled && index === itemConfigs.length - 1 ? null : item?.url}
        className={cx(
          s.link,
          {[s.parentBreadcrumb]: isParent},
          {[s.focused]: isDropdownOpen},
          {active: activeItemId && item?.id === activeItemId}
        )}>
        <span className={s.linkContent} data-test-id={index === itemConfigs.length - 1 ? 'breadcrumb-last-item' : null}>{item?.labelText}</span>
        {isParent && <i className={s.chevronDown}/>}
      </a>
    )
  }

  const getBreadcrumbItem = ({item, index}) => {
    if (item?.childBreadcrumbs?.length > 0 && enableChildBreadcrumb) {
      return (
        <Dropdown
          trigger='hover'
          contentBoxClass={cx({[s.dropdownContent]: isDropdownOpen})}
          content={item?.childBreadcrumbs?.map((breadcrumb) => getBreadcrumbLinkItem({item: breadcrumb}))}
          onOpenStatusChange={(isOpen) => setIsDropdownOpen(isOpen)}>
          {getBreadcrumbLinkItem({item, index, isParent: true})}
        </Dropdown>
      )
    }

    return getBreadcrumbLinkItem({item, index})
  }

  const getNewLayoutBreadcrumbs = () => {
    const hasRootItem = itemConfigs?.some(itemConfig => itemConfig?.isRoot);

    return itemConfigs?.map((itemConfig, index) =>
      itemConfig?.isRoot ?
        (
          <li key={'root'} className={cx(s.linkListItem, s.rootLinkListItem)} onClick={() => sendDavinciEvent(itemConfig?.labelText)}>
            <a href={itemConfig?.url} title={'Anasayfa'}>
              <i className={s.breadcrumbHome}/>
            </a>
          </li>
        ) : (
          <li
            key={itemConfig?.id}
            className={s.linkListItem}
            onClick={() => sendDavinciEvent(itemConfig?.labelText, index)}>
            {((hasRootItem && index !== 1) || (!hasRootItem && index !== 0)) && <div className={s.linkListItemNavigationIcon}/>}
            {
              getBreadcrumbLinkItem({item: itemConfig, index})
            }
          </li>
        )
    )
  }

  return (
    <div className={cx(s.root, className, {[s.rootIconEnabled]: rootIconEnabled}, s[size])}>
      {
        isSchemaStructuredActive &&
        <HbBreadCrumbStructuredData type='BreadcrumbList' itemListElement={getStructData()}/>
      }
      <HbHorizontalScrollView
        rootElementType='ul'
        initialFocusedItemIndex={itemConfigs.length - 1}
        className={cx(s.linkList, {[s.hasChildBreadcrumb]: itemConfigs?.some((item) => item?.childBreadcrumbs?.length > 0 && enableChildBreadcrumb)})}>
        {
          showPLPRedesignLayout ?
            getNewLayoutBreadcrumbs() :
            itemConfigs?.map((itemConfig, index) =>
              itemConfig.isRoot ? renderRootLink(itemConfig) : (
                <li
                  key={itemConfig.id}
                  className={s.linkListItem}
                  onClick={() => sendDavinciEvent(itemConfig.labelText, index)}>
                  <div className={s.linkListItemNavigationIcon}/>
                  {
                    location && index === itemConfigs.length - 1 && `${location.pathname}${location.search}` === itemConfig.url ? (
                      <span className={s.labelText} data-test-id={'breadcrumb-last-item'}>{itemConfig.labelText}</span>
                    ) :
                      getBreadcrumbItem({item: itemConfig, index})
                  }
                </li>
              ))
        }
      </HbHorizontalScrollView>
    </div>
  )
})

HbBreadCrumbView.propTypes = {
  className: PropTypes.string,
  activeItemId: PropTypes.string,
  itemConfigs: PropTypes.array.isRequired,
  isSchemaStructuredActive: PropTypes.bool,
  rootIconEnabled: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(BREADCRUMB_SIZES)),
  lastItemDisabled: PropTypes.bool,
  forceRedirect: PropTypes.bool
}

HbBreadCrumbView.defaultProps = {
  isSchemaStructuredActive: true,
  rootIconEnabled: true,
  size: BREADCRUMB_SIZES.SMALL,
  forceRedirect: false
}

export default HbBreadCrumbView
